import { useMutation, useQuery } from '@apollo/client';
import { adminBrandQuery } from '../../graphql/queries';
import { useContext, useState } from 'react';
import { Alert, Button } from 'reactstrap';
// @ts-ignore
import nestedProperty from 'nested-property';
import i18n from '@/utils/i18n';
import {
  enableBrandMutation,
  removeBrandMutation,
  requestUploadUrl,
  updateBrandMutation,
} from '../../graphql/mutations';
import { uploadToS3 } from '@/utils/imageUpload';
import { ButtonWithConfirmation } from '../../components/ButtonWithConfirmation';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { FaExclamationTriangle } from 'react-icons/fa';
import { ContentSection } from '../../components/Layout/ContentSection';

type MerchantEditGeneralInfoTabProps = {
  externalPickpackId: string | null;
};

export const MerchantEditGeneralInfoTab: React.FC<
  MerchantEditGeneralInfoTabProps
> = ({ externalPickpackId }) => {
  const [brandUpdate, setBrandUpdate] = useState<any | undefined>(null);
  const [requestUploadUrlQuery] = useMutation(requestUploadUrl);
  const [updateBrand, { loading: updating }] = useMutation(updateBrandMutation);
  const [removeBrand, { loading: removingBrand }] =
    useMutation(removeBrandMutation);
  const [enableBrand, { loading: enablingBrand }] =
    useMutation(enableBrandMutation);
  const [updateBrandLogo, { loading: updatingBrandLogo }] =
    useMutation(updateBrandMutation);
  const [removeUpdateBrandLogo, { loading: removingBrandLogo }] =
    useMutation(updateBrandMutation);
  const viewer = useContext(ViewerContext);
  const { data = { brand: null }, loading: loadingBrand } = useQuery(
    adminBrandQuery,
    {
      fetchPolicy: 'no-cache',
      variables: { id: externalPickpackId },
      onCompleted: (brandData) => {
        // @ts-ignore
        setBrandUpdate(brandData.brand);
      },
    },
  );

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;
    if (isNumber) formattedValue = Number(value);
    if (isDate) {
      try {
        formattedValue = new Date(value).toISOString();
      } catch {}
    }

    setBrandUpdate((oldBrandUpdate: any) => {
      let newBrandUpdate = { ...oldBrandUpdate };
      nestedProperty.set(newBrandUpdate, name, formattedValue);
      return newBrandUpdate;
    });
  }

  function handleSave() {
    const mutationInput = {
      name: brandUpdate.name,
      logo: brandUpdate.logo,
    };

    updateBrand({
      variables: {
        id: brandUpdate.id,
        brand: mutationInput,
      },
      update: () => {},
    }).catch((err) => {
      alert(i18n.t('Merchants:errorUpdatingBrand', { error: err }));
    });
  }

  function removeBrandLogo() {
    if (!window.confirm(i18n.t('Merchants:confirmRemoveBrandLogo'))) {
      return;
    }
    removeUpdateBrandLogo({
      variables: {
        id: brandUpdate.id,
        brand: {
          logo: null,
        },
      },

      update: () => {
        setBrandUpdate((oldBrandUpdate: any) => ({
          ...oldBrandUpdate,
          logo: null,
        }));
      },
    }).catch((err) => {
      alert(i18n.t('Merchants:errorRemovingBrandLogo', { error: err }));
    });
  }

  function toggleMerchantRemoved() {
    const mutationOptions = {
      variables: {
        id: brandUpdate.id,
      },
    };
    const promise = brandUpdate.permanently_removed
      ? enableBrand(mutationOptions)
      : removeBrand(mutationOptions);
    promise.then(() => {
      window.location.reload();
    });
  }

  const onUploadBrandImage = async (e: any) => {
    const { files } = e.target;
    const file = (files || [])[0];
    if (file) {
      const { name: filename, type: filetype } = file;
      const { data } = await requestUploadUrlQuery({
        variables: { filename: externalPickpackId, filetype },
      });
      if (data.requestUploadUrl) {
        const url = await uploadToS3(file, data.requestUploadUrl);
        updateBrandLogo({
          variables: {
            id: brandUpdate.id,
            brand: {
              logo: url,
            },
          },

          update: () => {
            setBrandUpdate((oldBrandUpdate: any) => ({
              ...oldBrandUpdate,
              logo: url,
            }));
          },
        }).catch((err) => {
          alert(i18n.t('Merchants:errorUpdatingBrand', { error: err }));
        });
      } else {
        alert(i18n.t('Merchants:couldNotRequestUploadUrl'));
      }
    }
  };

  if (!externalPickpackId) {
    return <p>{i18n.t('Merchants:noExternalPickpackId')}</p>;
  }

  // @ts-ignore
  if (
    loadingBrand ||
    !brandUpdate ||
    data.brand === null ||
    data.brand.id !== brandUpdate.id
  ) {
    return (
      <div style={{ minHeight: '100px' }} className="p-4">
        <LoadingContainer />
      </div>
    );
  }

  const allowedToAccess = CanAccessPartnerData(viewer);

  return (
    <ContentSection title={i18n.t('Common:generalInfo')} collapsable={false}>
      <EditSection onSave={handleSave} isLoading={updating}>
        <EditItem title={i18n.t('Merchants:merchantName')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:name')}
            </span>
            <input
              type="text"
              className="form-control"
              name="name"
              value={brandUpdate.name || ''}
              onChange={handleChange}
            />
          </div>
        </EditItem>
        <EditItem title={i18n.t('Merchants:merchantLogo')}>
          {brandUpdate.logo && (
            <button
              className="btn btn-danger btn-sm mb-2"
              onClick={removeBrandLogo}
              disabled={removingBrandLogo}
            >
              {i18n.t('Merchants:removeLogo')}
            </button>
          )}
          <div className="input-group mb-3">
            <input type="file" onChange={onUploadBrandImage} />
            <br />
            {updatingBrandLogo && <p>{i18n.t('Merchants:updating')}</p>}
          </div>
        </EditItem>
        <EditItem title={i18n.t('Merchants:other')}>
          {brandUpdate.permanently_removed && (
            <Alert color="warning" className="mb-3">
              <div className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                <span>{i18n.t('Merchants:brandRemovedUsersWarning')}</span>
              </div>
            </Alert>
          )}

          <ButtonWithConfirmation
            style={{ marginTop: '0.3em' }}
            color="danger"
            onConfirmed={toggleMerchantRemoved}
            confirmationText={i18n.t('Merchants:confirmToggleMerchant', {
              action: i18n.t(
                `Merchants:${
                  brandUpdate.permanently_removed ? 'enable' : 'disable'
                }`,
              ),
              merchantName: brandUpdate.name,
            })}
          >
            {i18n.t(
              `Merchants:${
                brandUpdate.permanently_removed ? 'enable' : 'disable'
              }Merchant`,
            )}
          </ButtonWithConfirmation>
        </EditItem>
        {allowedToAccess && externalPickpackId && (
          <pre>
            {i18n.t('Merchants:brandIdLabel', {
              brandId: externalPickpackId,
            })}
          </pre>
        )}
        {allowedToAccess && externalPickpackId && (
          <pre>
            {i18n.t('Merchants:vytalBrandIdLabel', {
              vytalBrandId: brandUpdate.external_vytal_id,
            })}
          </pre>
        )}
        <Button onClick={handleSave} className="float-end">
          {i18n.t('Merchants:saveBrandGeneralInfo')}
        </Button>
      </EditSection>
    </ContentSection>
  );
};
